import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  Megaphone,
  Radio,
  Send,
  Target,
  CheckCircle2,
  XCircle,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  CalendarDays,
} from "lucide-react";
import { AdminDashboardResponse } from "./types";
import useRequest from "../../../components/hook/use-req";
import GlobalAdminStatCard from "../../../components/marketing/global-admin/stat-card";
import { formatCurrency } from "../../../utils/function";
import TopFundedOrgs from "../../../components/marketing/global-admin/top-funded-orgs";


const pct = (n?: number) => `${(n ?? 0).toFixed(1)}%`;

// GET /admin/dashboard — platform-wide overview for global_admin, styled to
// match the existing CPaaS dashboard's "frosted glass" card language (see
// pages/cloud-dashboard/cpas-redirect.tsx + components/marketing/cloud-dashboard/*)
// so this new area still feels like part of the same product.
const GlobalAdminDashboard: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [data, setData] = useState<AdminDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const { makeRequest: getDashboard } = useRequest("/admin/dashboard", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = (await getDashboard()) as [AdminDashboardResponse, number];
      if (response?.status) setData(response);
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inflowUp = data?.finance.inflow_direction !== "decrease";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950 -m-4 p-4">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm px-6 py-5 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Global Admin Dashboard
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Platform-wide campaigns, performance and finance overview.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !data ? (
        <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-10 text-center">
          <p className="text-sm text-slate-500 dark:text-gray-400">Couldn't load the dashboard. Please try again shortly.</p>
        </div>
      ) : (
        <>
          {/* Campaigns */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-3">Campaigns</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <GlobalAdminStatCard
                label="Total Campaigns"
                value={data.campaigns.total.toLocaleString()}
                icon={<Megaphone className="w-6 h-6 text-blue-600" />}
                iconClassName="bg-blue-100 dark:bg-blue-500/15"
              />
              <GlobalAdminStatCard
                label="Active Campaigns"
                value={data.campaigns.active.toLocaleString()}
                icon={<Radio className="w-6 h-6 text-emerald-600" />}
                iconClassName="bg-emerald-100 dark:bg-emerald-500/15"
              />
              <GlobalAdminStatCard
                label="SMS Sent Today"
                value={data.campaigns.sms_sent_today.toLocaleString()}
                icon={<Send className="w-6 h-6 text-indigo-600" />}
                iconClassName="bg-indigo-100 dark:bg-indigo-500/15"
              />
            </div>
          </section>

          {/* Performance */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-3">Performance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <GlobalAdminStatCard
                label="Total Attempted"
                value={data.performance.total_attempted.toLocaleString()}
                icon={<Target className="w-6 h-6 text-slate-600" />}
                iconClassName="bg-slate-100 dark:bg-gray-800"
              />
              <GlobalAdminStatCard
                label="Delivery Rate"
                value={pct(data.performance.delivery_rate_pct)}
                valueClassName="text-emerald-600 dark:text-emerald-400"
                icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                iconClassName="bg-emerald-100 dark:bg-emerald-500/15"
              />
              <GlobalAdminStatCard
                label="Rejected Rate"
                value={pct(data.performance.failed_rate_pct)}
                valueClassName="text-rose-600 dark:text-rose-400"
                icon={<XCircle className="w-6 h-6 text-rose-600" />}
                iconClassName="bg-rose-100 dark:bg-rose-500/15"
              />
              <GlobalAdminStatCard
                label="Conversion Rate"
                value={pct(data.performance.conversion_rate_pct)}
                icon={<Target className="w-6 h-6 text-purple-600" />}
                iconClassName="bg-purple-100 dark:bg-purple-500/15"
              />
            </div>
          </section>

          {/* Finance */}
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-3">Finance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              <GlobalAdminStatCard
                label="Today's Inflow"
                value={formatCurrency(data.finance.today_inflow, "NGN", "symbol")}
                icon={<Wallet className="w-6 h-6 text-blue-600" />}
                iconClassName="bg-blue-100 dark:bg-blue-500/15"
              />
              <GlobalAdminStatCard
                label="Yesterday's Inflow"
                value={formatCurrency(data.finance.yesterday_inflow, "NGN", "symbol")}
                icon={<CalendarDays className="w-6 h-6 text-slate-600" />}
                iconClassName="bg-slate-100 dark:bg-gray-800"
              />
              <GlobalAdminStatCard
                label="Inflow Change"
                value={pct(Math.abs(data.finance.inflow_change_pct))}
                valueClassName={inflowUp ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}
                sublabel={
                  <span className={`inline-flex items-center gap-1 ${inflowUp ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {inflowUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    vs yesterday
                  </span>
                }
                icon={
                  inflowUp ? (
                    <ArrowUpRight className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <ArrowDownRight className="w-6 h-6 text-rose-600" />
                  )
                }
                iconClassName={inflowUp ? "bg-emerald-100 dark:bg-emerald-500/15" : "bg-rose-100 dark:bg-rose-500/15"}
              />
            </div>

            <TopFundedOrgs orgs={data.finance.top_funded_orgs} />
          </section>
        </>
      )}
    </div>
  );
};

export default GlobalAdminDashboard;
